import { createContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import useAxios from "../../utils/useAxios";
import { resObj } from "../../utils/constants";

const PayloadRawMaterialContext = createContext();

export default PayloadRawMaterialContext;

export const PayloadRawMaterialProvider = ({ children }) => {
  const api = useAxios();
  const navigate = useNavigate();

  const initValues = {
    operatorArray: [],
    bufferUniqueIdArray: [],
    payloadNameArray: [],
    payloadTypeArray: [],
    reporterArray: [],
    payloadBatchNumberArray: [],
  };
  const [initialValues, setInitialValues] = useState(initValues);

  const payloadRawMaterialObj = {
    payloadName: '',
    payloadType: '',
    reporter: '',
    payloadBatchNumber: '',
    storageBufferId: null, 
    concentration: '', 
    operatorArray: [],
    storageBufferAcronym: '',
    molecularWeight: '',
  };
  const [payloadRawData, setPayloadRawData] = useState(payloadRawMaterialObj);
  const [apiRes, setApiRes] = useState(resObj);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmit, setIsSubmit] = useState(false);
  const [payloadRawMaterialId, setPayloadRawMaterialId] = useState('');

  useEffect(() => {
    getInitialData();
    getOperators();
  }, []);

  const getInitialData = async () => {
    try {
      setApiRes({...apiRes, loading: true});
      const response = await api.get(
        '/api/materials-capture/payload/initial/data/get'
      );
      
      if (response.status === 200) {
        setInitialValues(prev => ({
          ...prev,
          bufferUniqueIdArray: response.data.buffer_list,
          payloadNameArray: response.data.payload_name_list,
          payloadTypeArray: response.data.payload_type_list,
          reporterArray: response.data.reporter_list,
          payloadBatchNumberArray: response.data.payload_batch_number_list,
        }));
        setApiRes({ ...apiRes, loading: false });
      }
    } catch (err){
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Payload Raw Materials Init Data Get',
        loading: false,
      });
    }
  };

  const getOperators = async () => {
    try {
      setApiRes({...apiRes, loading: true});
      const response = await api.get('/api/materials-capture/operators/get');
      
      if (response.status === 200) {        
        setInitialValues(prev => ({
          ...prev,
          operatorArray: response.data,
        }))
        setApiRes({ ...apiRes, loading: false });
      }
    } catch (err){
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Operators Get',
        loading: false,
      });
    }
  };

  const validate = () => {    
    const errors = {};

    if (!payloadRawData?.payloadName) {
      errors['payloadName'] = 'Payload Name is required!';
    }
    if (!payloadRawData?.payloadType) {
      errors['payloadType'] = 'Payload Type is required!';
    }
    if (!payloadRawData?.reporter) {
      errors['reporter'] = 'Reporter is required!';
    }
    if (!payloadRawData?.payloadBatchNumber) {
      errors['payloadBatchNumber'] = 'Payload Batch Number is required!';
    }
    if (!payloadRawData?.storageBufferId) {
      errors['storageBufferId'] = 'Storage Buffer Id is required!';
    }
    if (!payloadRawData?.concentration) {
      errors['concentration'] = 'Concentration is required!';
    }
    if (!payloadRawData?.molecularWeight) {
      errors['molecularWeight'] = 'Molecular weight (g/mol) is required!';
    }
    if (payloadRawData?.operatorArray.length === 0) {
      errors['operator'] = 'Operator is required!';
    }

    return errors;
  };

  const handleSubmit = () => {
    setFormErrors(validate());
    setIsSubmit(true);
  };

  useEffect(() => {
    if (Object.keys(formErrors).length === 0 && isSubmit) {
      submitBuffer();
    }
  }, [formErrors]);

  useEffect(() => {  
    const bufferAcronym = initialValues?.bufferUniqueIdArray.find(
      item => item.unique_id === payloadRawData?.storageBufferId
    )?.buffer_acronym ?? '';

    setPayloadRawData(prev => ({
      ...prev,
      storageBufferAcronym: bufferAcronym
    }));
  }, [payloadRawData?.storageBufferId, initialValues?.bufferUniqueIdArray]);

  const submitBuffer = () => {
    if (payloadRawMaterialId === '') {
      createPayloadRawMaterial();
    } else {
      updatePayloadRawMaterial();
    }
  };

  const createPayloadRawMaterial = async () => {
    try {
      setApiRes({...apiRes, loading: true});

      const response = await api.post(
        '/api/materials-capture/payload-raw-material/create', {
        payload_name: payloadRawData?.payloadName,
        payload_type: payloadRawData?.payloadType,
        reporter: payloadRawData?.reporter,
        payload_batch_number: payloadRawData?.payloadBatchNumber,
        storage_buffer_id: payloadRawData?.storageBufferId,
        concentration: payloadRawData?.concentration,
        operator_list: payloadRawData?.operatorArray,
        storage_buffer_acronym: payloadRawData?.storageBufferAcronym,
        molecular_weight: payloadRawData?.molecularWeight,
      });

      if (response.status === 200) {
        setApiRes({
          ...apiRes,
          showAlert: true,
          successMsg: response.data,
          loading: false,
        });
        navigate('/payload-raw-materials');
      }
    } catch (err) {
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Payload Raw Material create',
        loading: false,
      });
    }
  };

  const updatePayloadRawMaterial = async () => {
    try {
      setApiRes({...apiRes, loading: true});

      const response = await api.post('/api/materials-capture/payload-raw-material/edit', {
        payload_raw_material_id: payloadRawMaterialId,
        payload_name: payloadRawData?.payloadName,
        payload_type: payloadRawData?.payloadType,
        reporter: payloadRawData?.reporter,
        payload_batch_number: payloadRawData?.payloadBatchNumber,
        storage_buffer_id: payloadRawData?.storageBufferId,
        concentration: payloadRawData?.concentration,
        operator_list: payloadRawData?.operatorArray,
        storage_buffer_acronym: payloadRawData?.storageBufferAcronym,
        molecular_weight: payloadRawData?.molecularWeight,
      });

      if (response.status === 200) {
        setApiRes({
          ...apiRes,
          showAlert: true,
          successMsg: response.data,
          loading: false,
        });
      }
    } catch (err) {
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Payload Raw Materials update',
        loading: false,
      });
    }
  };

  useEffect(() => {
    if (payloadRawMaterialId !== '') getPayloadRawMaterialDetails()
  }, [payloadRawMaterialId]);

  const getPayloadRawMaterialDetails = async () => {
    try {
      setApiRes({...apiRes, loading: true});
      const response = await api.get(
        '/api/materials-capture/payload-raw-material/details/get', {
          params: {
            payload_raw_material_id: payloadRawMaterialId
          }
        });
      
      if (response.status === 200) {
        const payloadRawData = response.data;
        setPayloadRawData(prev => ({
          ...prev,
          payloadName: payloadRawData.payload_name,
          payloadType: payloadRawData.payload_type,
          reporter: payloadRawData.reporter,
          payloadBatchNumber: payloadRawData.payload_batch_number,
          storageBufferId: payloadRawData.storage_buffer_unique_id,
          concentration: payloadRawData.concentration,
          molecularWeight: payloadRawData.molecular_weight,
          operatorArray: payloadRawData.operator_list,
        }));
        setApiRes({ ...apiRes, loading: false });
      }
    } catch (err){
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Payload Raw Material Details Get',
        loading: false,
      });
    }
  };

  const contextData = {
    initialValues: initialValues,
    payloadRawData: payloadRawData,
    setPayloadRawData: setPayloadRawData,
    formErrors: formErrors,
    handleSubmit: handleSubmit,
    apiRes: apiRes,
    setApiRes: setApiRes,
    setPayloadRawMaterialId: setPayloadRawMaterialId,
  };

  return (
    <PayloadRawMaterialContext.Provider value={contextData}>
      {children}
    </PayloadRawMaterialContext.Provider>
  )
};